import {
    collection,
    doc,
    setDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    getDoc
} from "firebase/firestore";
import { db } from "../../Firebase";
import {
    kSequencesCollectionName,
    kSequenceLayerClipsSubcollectionName,
    kSequenceLayersSubcollectionName
} from "../../utils/CollectionConstants";
import {SequenceStateStore} from "../store/SequenceStateStore";
import { buildReferencedAssetIds } from "../../storage/AssetReferenceUsage";

// Helper: get current time as Unix seconds (int64-compatible for Go server)
const nowUnixSec = () => Math.floor(Date.now() / 1000);


type EngineClipLike = any;

/**
 * Firestore persistence for Sequence and its Clips.
 * - Call setActiveSequence() when opening a sequence so subsequent writes target it.
 * - Methods are idempotent/upserts where appropriate.
 */
export class SequencePersistenceService {
    private static activeSequenceId: string | null = null;
    private static userId: string | null = null;
    private static workspaceId: string | null = null;
    private static initialized: boolean = false;

    static init(userId: string | null, sequenceId: string | null, workspaceId?: string | null): void {
        if (!userId) {
            console.warn('[SequencePersistenceService] init called without userId');
        }
        this.userId = userId || null;
        this.activeSequenceId = sequenceId || null;
        this.workspaceId = workspaceId || null;
        this.initialized = true;
    }

    static reset(): void {
        this.userId = null;
        this.activeSequenceId = null;
        this.workspaceId = null;
        this.initialized = false;
    }

    static async setOrderLayerIds(orderedLayerIds: string[]): Promise<void> {
        if (!this.activeSequenceId || !this.userId) return;
        const seqRef = doc(db, kSequencesCollectionName, this.activeSequenceId);
        await setDoc(
            seqRef,
            {
                userId: this.userId, // Ensure userId is preserved on merge
                ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
                updatedAt: nowUnixSec(),
                orderedLayerIds: orderedLayerIds
            },
            { merge: true }
        );
    }

    /**
     * Persist the ordered clip IDs for a single layer.
     * This enables restore without listing the clips subcollection.
     */
    static async setOrderedClipIds(layerId: string, orderedClipIds: string[]): Promise<void> {
        if (!this.activeSequenceId || !this.userId || !layerId) return;

        const cleaned = Array.isArray(orderedClipIds)
            ? orderedClipIds.filter((id: any) => typeof id === "string" && id.length > 0)
            : [];

        const nowSec = nowUnixSec();

        try {
            const layerRef = doc(
                db,
                kSequencesCollectionName, this.activeSequenceId,
                kSequenceLayersSubcollectionName, layerId,
            );

            try {
                await updateDoc(layerRef, {
                    updatedAt: nowSec,
                    orderedClipIds: cleaned,
                });
            } catch (e: any) {
                // If the layer doc doesn't exist yet, create it.
                if (e?.code === "not-found") {
                    await setDoc(layerRef, {
                        id: layerId,
                        sequenceId: this.activeSequenceId,
                        userId: this.userId,
                        ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
                        createdAt: nowSec,
                        updatedAt: nowSec,
                        orderedClipIds: cleaned,
                    }, { merge: true });
                } else {
                    throw e;
                }
            }
        } catch (e) {
            console.error("[SequencePersistenceService] Error setting ordered clip IDs:", { sequenceId: this.activeSequenceId, layerId }, e);
        }
    }

    static async addLayer(layerId: string, name?: string) {
        if (!this.activeSequenceId || !layerId || !this.userId) return;
        const nowSec = Math.floor(Date.now() / 1000);
        const trackRef = doc(collection(doc(db, kSequencesCollectionName, this.activeSequenceId), kSequenceLayersSubcollectionName), layerId);
        await setDoc(trackRef, {
            id: layerId,
            sequenceId: this.activeSequenceId,
            updatedAt: nowSec,
            userId: this.userId,
            ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
            name: name || null,
        }, { merge: true });
    }

    static async updateLayerName(layerId: string, name: string) {
        if (!this.activeSequenceId || !layerId || !this.userId) return;
        const nowSec = Math.floor(Date.now() / 1000);
        const trackRef = doc(collection(doc(db, kSequencesCollectionName, this.activeSequenceId), kSequenceLayersSubcollectionName), layerId);
        await updateDoc(trackRef, {
            name,
            updatedAt: nowSec,
            // Note: userId is not included in update to avoid triggering security rule check
            // Security rules verify existing userId matches request.auth.uid
        });
    }

    static async deleteLayer(layerId: string) {
        if (!this.activeSequenceId || !layerId || !this.userId) return;
        const trackRef = doc(collection(doc(db, kSequencesCollectionName, this.activeSequenceId), kSequenceLayersSubcollectionName), layerId);
        await deleteDoc(trackRef);
    }

    static async addClip(clip: EngineClipLike, layerId: string) {
        if (!this.activeSequenceId || !clip?.id || !layerId || !this.userId) return;
        const nowSec = Math.floor(Date.now() / 1000);
        const clipRef = doc(collection(doc(collection(doc(db, kSequencesCollectionName, this.activeSequenceId), kSequenceLayersSubcollectionName), layerId), kSequenceLayerClipsSubcollectionName), clip.id);
        // Lookup assetId from store if available
        const assetId = this.lookupAssetId(clip.id);
        const mapped = this.mapClipForStorage(clip);
        // Get waveformColor from store
        const waveformColor = SequenceStateStore.getWaveformColor(clip.id);
        const referencedAssetIds = buildReferencedAssetIds({ assetId });
        await setDoc(clipRef, {
            id: clip.id,
            sequenceId: this.activeSequenceId,
            layerId,
            type: String(clip.type ?? "unknown"),
            assetId: assetId ?? null,
            referencedAssetIds,
            name: clip.name ?? "",
            updatedAt: nowSec,
            userId: this.userId,
            ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
            waveformColor: waveformColor ?? null,
            ...mapped,
        }, { merge: true });
    }

    /**
     * Add a clip record directly (bypasses SequenceStateStore clipId->assetId mapping).
     * Useful for bootstrapping a new sequence with known assetIds/trackIds.
     */
    static async addClipRecord(args: {
        clipId: string;
        layerId: string;
        type: string;
        assetId: string;
        name?: string;
        startSec?: number;
        inSec?: number;
        outSec?: number;
        offsetSec?: number;
        durationSec?: number;
        trimmedDurationSec?: number;
        props?: any;
        media?: any;
        waveformColor?: string | null;
    }): Promise<void> {
        if (!this.activeSequenceId || !this.userId) return;
        const {
            clipId,
            layerId,
            type,
            assetId,
            name,
            startSec,
            inSec,
            outSec,
            offsetSec,
            durationSec,
            trimmedDurationSec,
            props,
            media,
            waveformColor,
        } = args;
        if (!clipId || !layerId || !type || !assetId) return;

        const toFiniteNum = (value: any, fallback: number): number => {
            const n = typeof value === "number" ? value : Number(value);
            return Number.isFinite(n) ? n : fallback;
        };

        const inS = toFiniteNum(inSec, 0);
        const outS = toFiniteNum(outSec, inS);
        const dur = toFiniteNum(durationSec, Math.max(0, outS - inS));
        const trimmedDur = toFiniteNum(trimmedDurationSec, dur);
        const nowSec = nowUnixSec();
        const referencedAssetIds = buildReferencedAssetIds({ assetId });

        const clipRef = doc(
            db,
            kSequencesCollectionName, this.activeSequenceId,
            kSequenceLayersSubcollectionName, layerId,
            kSequenceLayerClipsSubcollectionName, clipId
        );

        const updatePayload = {
            updatedAt: nowSec,
            type: String(type),
            assetId: String(assetId),
            referencedAssetIds,
            name: String(name ?? ""),
            startSec: toFiniteNum(startSec, 0),
            durationSec: dur,
            trimmedDurationSec: trimmedDur,
            inSec: inS,
            outSec: outS,
            offsetSec: toFiniteNum(offsetSec, 0),
            props: props ?? {},
            media: media ?? {},
            waveformColor: waveformColor ?? null,
            // Note: userId is not included in update to avoid triggering security rule check
        };

        try {
            await updateDoc(clipRef, updatePayload);
        } catch (e: any) {
            if (e?.code === "not-found") {
                await setDoc(
                    clipRef,
                    {
                        id: clipId,
                        sequenceId: this.activeSequenceId,
                        layerId,
                        userId: this.userId,
                        ...(this.workspaceId ? { workspaceId: this.workspaceId } : {}),
                        createdAt: nowSec,
                        ...updatePayload,
                    },
                    { merge: true }
                );
            } else {
                console.error("[SequencePersistenceService] Error adding clip record:", { sequenceId: this.activeSequenceId, layerId, clipId }, e);
                throw e;
            }
        }
    }

    static async updateClip(clip: EngineClipLike) {
        if (!this.activeSequenceId || !clip?.id || !this.userId) return;
        const nowSec = Math.floor(Date.now() / 1000);
        const layerId: string | undefined = String(clip?.track?.id ?? "");
        if (!layerId) return;
        const clipRef = doc(collection(doc(collection(doc(db, kSequencesCollectionName, this.activeSequenceId), kSequenceLayersSubcollectionName), layerId), kSequenceLayerClipsSubcollectionName), clip.id);
        const assetId = this.lookupAssetId(clip.id);
        const mapped = this.mapClipForStorage(clip);
        // Get waveformColor from store
        const waveformColor = SequenceStateStore.getWaveformColor(clip.id);
        const referencedAssetIds = buildReferencedAssetIds({ assetId });
        await updateDoc(clipRef, {
            updatedAt: nowSec,
            assetId: assetId ?? null,
            referencedAssetIds,
            waveformColor: waveformColor ?? null,
            ...mapped,
            // Note: userId is not included in update to avoid triggering security rule check
            // Security rules verify existing userId matches request.auth.uid
        });
    }

    // Track pending deletes to prevent duplicate calls
    private static pendingClipDeletes = new Set<string>();

    static async deleteClip(layerId: string, clipId: string) {
        if (!this.activeSequenceId || !layerId || !clipId || !this.userId) return;

        // Prevent duplicate delete calls
        const deleteKey = `${this.activeSequenceId}/${layerId}/${clipId}`;
        if (this.pendingClipDeletes.has(deleteKey)) {
            console.warn("[SequencePersistenceService] Duplicate delete call ignored for clip:", clipId);
            return;
        }
        this.pendingClipDeletes.add(deleteKey);

        // Direct document reference - no query needed
        const clipRef = doc(
            db,
            kSequencesCollectionName, this.activeSequenceId,
            kSequenceLayersSubcollectionName, layerId,
            kSequenceLayerClipsSubcollectionName, clipId
        );

        try {
            await deleteDoc(clipRef);
            console.log("[SequencePersistenceService] Clip deleted successfully:", clipId);
        } catch (e) {
            console.error("[SequencePersistenceService] Error deleting clip:", e);
        } finally {
            this.pendingClipDeletes.delete(deleteKey);
        }
    }

    /**
     * Use orderedLayerIds on the sequence doc to return [[clipsOnLayer1], [clipsOnLayer2], ...]
     * Layers are kept in specified order; clips within each layer ordered by startSec asc.
     * Also returns layer metadata (name) for each layer.
     */
    static async fetchClipsByLayerOrdered(sequenceId?: string): Promise<{
        layers: Array<{ layerId: string; name: string | null; clips: any[] }>;
    }> {
        const sid = sequenceId ?? this.activeSequenceId;
        if (!sid || !this.userId) return { layers: [] };

        const seqRef = doc(db, kSequencesCollectionName, sid);
        let seqSnap;
        try {
            seqSnap = await getDoc(seqRef);
        } catch (e) {
            console.error("[SequencePersistenceService] Error fetching sequence doc:", { sequenceId: sid }, e);
            return { layers: [] };
        }
        if (!seqSnap.exists()) return { layers: [] };

        const data: any = seqSnap.data() || {};
        const orderedLayerIds: string[] = Array.isArray(data?.orderedLayerIds) ? data.orderedLayerIds : [];
        if (!orderedLayerIds.length) return { layers: [] };

        const layers: Array<{ layerId: string; name: string | null; clips: any[] }> = [];
        for (const layerId of orderedLayerIds) {
            const layerDocRef = doc(collection(seqRef, kSequenceLayersSubcollectionName), layerId);
            let layerSnap;
            try {
                layerSnap = await getDoc(layerDocRef);
            } catch (e) {
                console.warn("[SequencePersistenceService] Error fetching layer doc:", { sequenceId: sid, layerId, path: layerDocRef.path }, e);
                continue;
            }
            if (!layerSnap.exists()) continue;
            
            const layerData = layerSnap.data() || {};
            
            const layerName = layerData?.name ?? null;

            const clipsCol = collection(layerDocRef, kSequenceLayerClipsSubcollectionName);
            const orderedClipIds: string[] = Array.isArray(layerData.orderedClipIds)
                ? layerData.orderedClipIds.filter((id: any) => typeof id === "string" && id.length > 0)
                : [];

            let clips: any[] = [];
            if (orderedClipIds.length > 0) {
                const seen = new Set<string>();
                for (const clipId of orderedClipIds) {
                    if (seen.has(clipId)) continue;
                    seen.add(clipId);

                    try {
                        const clipDocRef = doc(clipsCol, clipId);
                        const clipSnap = await getDoc(clipDocRef);
                        if (!clipSnap.exists()) continue;
                        const c = clipSnap.data() || {};
                        clips.push({
                            id: c.id ?? clipSnap.id,
                            layerId,
                            type: c.type ?? "unknown",
                            assetId: c.assetId ?? null,
                            name: c.name ?? "",
                            startSec: Number(c.startSec ?? 0),
                            inSec: Number(c.inSec ?? 0),
                            outSec: Number(c.outSec ?? 0),
                            offsetSec: Number(c.offsetSec ?? 0),
                            props: c.props ?? {},
                            media: c.media ?? {},
                            waveformColor: c.waveformColor ?? null,
                        });
                    } catch (e) {
                        console.warn("[SequencePersistenceService] Error fetching clip doc:", { sequenceId: sid, layerId, clipId }, e);
                    }
                }
            } else {
                // Back-compat for older layers that don't have orderedClipIds yet.
                let snaps;
                try {
                    snaps = await getDocs(clipsCol);
                } catch (e) {
                    console.warn("[SequencePersistenceService] Error fetching layer clips:", { sequenceId: sid, layerId, path: clipsCol.path }, e);
                    continue;
                }

                clips = snaps.docs.map(d => {
                    const c = d.data() || {};
                    return {
                        id: c.id ?? d.id,
                        layerId,
                        type: c.type ?? "unknown",
                        assetId: c.assetId ?? null,
                        name: c.name ?? "",
                        startSec: Number(c.startSec ?? 0),
                        inSec: Number(c.inSec ?? 0),
                        outSec: Number(c.outSec ?? 0),
                        offsetSec: Number(c.offsetSec ?? 0),
                        props: c.props ?? {},
                        media: c.media ?? {},
                        waveformColor: c.waveformColor ?? null,
                    };
                });
            }
            // Keep deterministic ordering
            clips.sort((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0));
            layers.push({ layerId, name: layerName, clips });
        }
        return { layers };
    }

    private static mapClipForStorage(clip: EngineClipLike) {
        const isMedia = ('range' in clip) && ('offset' in clip);
        let startSec = 0;
        let durationSec = 0;
        let trimmedDurationSec = 0;
        let inSec = 0;
        let outSec = 0;
        let offsetSec = 0;
        if (isMedia) {
            inSec = Number(clip.range?.[0]?.seconds ?? 0);
            outSec = Number(clip.range?.[1]?.seconds ?? inSec);
            offsetSec = Number(clip.offset?.seconds ?? 0);
            startSec = Number(clip.start?.seconds ?? 0);
            durationSec = Number(clip.duration?.seconds ?? Math.max(0, outSec - inSec));
            trimmedDurationSec = Number(clip.timelineDuration?.seconds ?? Math.max(0, outSec - inSec));
        } else {
            const s = Number(clip.start?.seconds ?? 0);
            const e = Number(clip.stop?.seconds ?? s);
            startSec = s;
            durationSec = Math.max(0, e - s);
            trimmedDurationSec = Number(clip.timelineDuration?.seconds ?? durationSec);
            inSec = 0;
            outSec = durationSec;
            offsetSec = 0;
        }
        return {
            startSec,
            durationSec,
            trimmedDurationSec,
            inSec,
            outSec,
            offsetSec,
            props: {
                playbackRate: Number(clip.playbackRate ?? 1),
                gain: Number(clip.gain ?? 1),
                alpha: Number(clip.alpha ?? 1),
            },
            media: {
                hasAudioStream: Boolean((clip as any).hasAudioStream),
            },
        };
    }

    private static lookupAssetId(clipId: string): string | undefined {
        try {
            return SequenceStateStore?.clipIdToAssetId?.[clipId];
        } catch {
            return undefined;
        }
    }
}
